// Driver "Hot zones" (demand heat map) reads.
//
// The phone never listens to /demand_heat: it does one-shot get() calls per
// refresh, at a cadence the client-side cache decides (mobile-app/src/services/
// demandHeatCache.js), so the feature's RTDB cost is a flat few KB per driver
// per refresh regardless of how long the home screen stays open.
//
//   fetchDemandHeat('summary') → { s }     reads demand_heat/s                 (~0.75 KB)
//   fetchDemandHeat('full')    → { s, f }  reads demand_heat/s + demand_heat/f (~6 KB)
//
// The full read is TWO exact-path reads pipelined with Promise.all on the
// same socket (one round trip), never a get() of the whole /demand_heat node:
// that node also carries the ops-only `_meta`, and a driver must not download
// (or be billed for) anything the phone does not render.
//
// Errors never throw — the caller is a UI hook that must keep rendering the
// last good snapshot. A rules rejection ("permission denied": rules not
// deployed yet, or the role is not allowed) is surfaced distinctly as
// { error: 'permission_denied' } so the client can stop retrying until the
// next cold start instead of burning its daily read budget.
import { get } from 'firebase/database';
import { firebase } from '../config/configureFirebase';

const objectOrNull = (v) => (v && typeof v === 'object' ? v : null);

// Portal only (admin health line on the Dashboard map): one-shot read of the
// publisher's _meta node (lastRun, cells, zones, liveTotal, alpha, ref, bytes).
// Resolves null on any error — the health line is decoration, never a blocker.
export const fetchDemandHeatMeta = async () => {
  const { demandHeatMetaRef } = firebase;
  try {
    const snap = await get(demandHeatMetaRef);
    return objectOrNull(snap.val());
  } catch (e) {
    return null;
  }
};

export const fetchDemandHeat = async (kind) => {
  const { demandHeatSummaryRef, demandHeatCellsRef } = firebase;
  const full = kind === 'full';
  try {
    if (full) {
      const [sSnap, fSnap] = await Promise.all([get(demandHeatSummaryRef), get(demandHeatCellsRef)]);
      return { s: objectOrNull(sSnap.val()), f: objectOrNull(fSnap.val()) };
    }
    const snap = await get(demandHeatSummaryRef);
    return { s: objectOrNull(snap.val()), f: null };
  } catch (e) {
    const text = String(e && (e.code || e.message) || '');
    return {
      error: /permission/i.test(text) ? 'permission_denied' : 'read_failed',
      message: e && e.message ? e.message : 'read failed',
    };
  }
};
